// src/pages/LandingPage.tsx
// GRAIA Landing Page — Header + Hero + Blocos + Footer
// Prefixo CSS: ldgpr-

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Instagram, MessageCircle, Twitter } from 'lucide-react';
import './LandingPage.css';

import HeroBlock from '../components/landing/HeroBlock';
import PlanosBlock from '../components/landing/PlanosBlock';
import FlashcardsBlock from '../components/landing/FlashcardsBlock';
import StudyModesBlock from '../components/landing/StudyModesBlock';
import PlannerBlock from '../components/landing/PlannerBlock';
import QuestoesBlock from '../components/landing/QuestoesBlock';
import ApostilasBlock from '../components/landing/ApostilasBlock';
import DesempenhoBlock from '../components/landing/DesempenhoBlock';
import AppBlock from '../components/landing/AppBlock';

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [footerVisible, setFooterVisible] = useState(false);
    const footerRef = useRef<HTMLElement>(null);

    // Header scroll
    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    // Footer IntersectionObserver — mesmo padrão dos blocos
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setFooterVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.08 } // dispara cedo pois o footer é alto
        );
        if (footerRef.current) observer.observe(footerRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="ldgpr-page">

            {/* ═══════════════════ HEADER ═══════════════════ */}
            <header className={`ldgpr-header ${scrolled ? 'ldgpr-header--scrolled' : ''}`}>
                <div className="ldgpr-header-inner">

                    <button className="ldgpr-logo" onClick={() => navigate('/')}>
                        <img src="/graia-logo.png" alt="GRAIA" className="ldgpr-logo-img" />
                        <div className="ldgpr-logo-text">
                            <span className="ldgpr-logo-name">GRAIA</span>
                            <span className="ldgpr-logo-sub">Medicina</span>
                        </div>
                    </button>

                    <div className="ldgpr-header-spacer" />

                    <nav className="ldgpr-nav-desktop">
                        <button className="ldgpr-btn-ghost" onClick={() => navigate('/login')}>
                            Entrar
                        </button>
                        <button className="ldgpr-btn-solid" onClick={() => navigate('/cadastro')}>
                            Começar Agora
                        </button>
                    </nav>

                    <button
                        className="ldgpr-hamburger"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Menu"
                    >
                        {menuOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                <div className={`ldgpr-mobile-menu ${menuOpen ? 'ldgpr-mobile-menu--open' : ''}`}>
                    <div className="ldgpr-mobile-divider" />
                    <button className="ldgpr-mobile-item ldgpr-mobile-item--muted"
                        onClick={() => { navigate('/login'); setMenuOpen(false); }}>
                        Entrar
                    </button>
                    <button className="ldgpr-mobile-cta"
                        onClick={() => { navigate('/cadastro'); setMenuOpen(false); }}>
                        Começar Agora
                    </button>
                </div>
            </header>

            {/* ═══════════════════ HERO ═══════════════════ */}
            <HeroBlock />

            {/* ═══════════════════ BLOCOS ═══════════════════ */}
            <div id="bloco-flashcards"> <FlashcardsBlock /> </div>
            <div id="bloco-modos">      <StudyModesBlock /> </div>
            <div id="bloco-apostilas">  <ApostilasBlock />  </div>
            <div id="bloco-questoes">   <QuestoesBlock />   </div>

            <PlanosBlock />

            <div id="bloco-planner">    <PlannerBlock />    </div>
            <div id="bloco-desempenho"> <DesempenhoBlock /> </div>
            <AppBlock />

            {/* ═══════════════════ FOOTER ═══════════════════ */}
            {/*
                ref={footerRef} + classe --visible adicionada pelo IntersectionObserver.
                Isso espelha o padrão de todos os blocos da landing.
            */}
            <footer
                className={`ldgpr-footer ${footerVisible ? 'ldgpr-footer--visible' : ''}`}
                ref={footerRef}
            >
                <div className="ldgpr-footer-inner">

                    <div className="ldgpr-footer-brand">
                        <div className="ldgpr-footer-logo-row">
                            <img src="/graia-logo.png" alt="GRAIA" className="ldgpr-footer-logo-img" />
                            <span className="ldgpr-footer-logo-name">GRAIA</span>
                        </div>
                        <p className="ldgpr-footer-tagline">
                            Estudo estruturado. Conhecimento consolidado.
                        </p>

                        <ul className="ldgpr-social-list">
                            <li>
                                <button className="ldgpr-social-link ldgpr-social-link--instagram" aria-label="Instagram">
                                    <Instagram size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--base" />
                                    <Instagram size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--fill" />
                                </button>
                            </li>
                            <li>
                                <button className="ldgpr-social-link ldgpr-social-link--whatsapp" aria-label="WhatsApp">
                                    <MessageCircle size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--base" />
                                    <MessageCircle size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--fill" />
                                </button>
                            </li>
                            <li>
                                <button className="ldgpr-social-link ldgpr-social-link--twitter" aria-label="Twitter / X">
                                    <Twitter size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--base" />
                                    <Twitter size={22} strokeWidth={1.75} className="ldgpr-social-icon ldgpr-social-icon--fill" />
                                </button>
                            </li>
                        </ul>
                    </div>

                    <nav className="ldgpr-footer-nav">
                        <div className="ldgpr-footer-col">
                            <span className="ldgpr-footer-col-title">Produto</span>
                            <button className="ldgpr-footer-link" onClick={() => navigate('/marketplace')}>Catálogo</button>
                            <button className="ldgpr-footer-link" onClick={() => navigate('/sobre')}>Como funciona</button>
                            <button className="ldgpr-footer-link" onClick={() => navigate('/termos')}>Termos de uso</button>
                        </div>
                        <div className="ldgpr-footer-col">
                            <span className="ldgpr-footer-col-title">Conta</span>
                            <button className="ldgpr-footer-link" onClick={() => navigate('/login')}>Entrar</button>
                            <button className="ldgpr-footer-link" onClick={() => navigate('/cadastro')}>Criar conta</button>
                        </div>
                    </nav>
                </div>

                <div className="ldgpr-footer-bottom">
                    <span>© 2025 GRAIA. Todos os direitos reservados.</span>
                </div>
            </footer>

        </div>
    );
};

export default LandingPage;